import type { Component } from "solid-js";
import { createMemo, createEffect, createSignal, getOwner, Match, runWithOwner, Show, Switch } from "solid-js";
import { type RouteSectionProps } from "@solidjs/router";
import ApiService from "../../services/ApiService";
import FolderId from "../../state/FolderId";
import IconSelect from "../../components/inputs/IconSelect";
import { MAX_CHARACTERS_MD, MAX_CHARACTERS_SM, SELECTABLE_FOLDER_ICONS } from "../../constants";
import PermissionService from "../../services/PermissionService";
import type { Folder } from "../../types/Folder";
import { folderSchema } from "../../types/Folder";
import { toLegacyPlatformIcon } from "../../utils/iconUtil";
import type { MoreAppError } from "../../MoreAppError";
import GenericFailureModal from "../GenericFailureModal";
import GlobalTabs from "../../components/tabs/GlobalTabs";
import { useTranslation } from "../../state/i18n";
import FolderPageContentForms from "./FolderPageContentForms";
import ResourcePermissionsContent from "./ResourcePermissionsContent";
import DeleteFolderButton from "./folder/DeleteFolderButton";
import InlineEditor from "../../components/inlineEditor/InlineEditor";
import Text from "../../components/text/Text";
import { IconButton, TextAndIconButton } from "../../components/button/Button";
import { pickSchema } from "../../utils/validationUtil";
import { NON_RETRYABLE_STATUS_CODES } from "../../utils/apiUtil";
import { isNil } from "lodash-es";
import { PillSingleSelect } from "../../components/menu/Select";
import { useCustomerState } from "../../state/customer";
import { useModalManager } from "../../components/modal/ModalManager";
import CreateFormWizard from "./CreateFormWizard";
import AddPermissionModal from "./AddPermissionModal";
import { useLocalUserSettingsDispatch } from "../../state/localUserSettings";
import { ResourceNotFoundContent } from "../ResourceNotFoundPage.tsx";
import { keyFromSelector } from "i18next";

const FolderPage: Component<RouteSectionProps<never>> = (props) => {
  const { t } = useTranslation();
  const [tab, setTab] = createSignal<string>("forms");
  const folderId = createMemo(() => FolderId.from(props.params.folderId!));
  const folderQuery = createMemo(() => ApiService.getFolder(folderId()));
  const setLocalUserSettings = useLocalUserSettingsDispatch();
  const customer = useCustomerState();
  const stringifiedLastCustomerId = customer.customerId.toString();

  createEffect(() => {
    const nextFolderId = folderId();
    if (!isNil(stringifiedLastCustomerId)) {
      setLocalUserSettings("lastFolderId", (currentId) => {
        if (!isNil(currentId) && currentId[stringifiedLastCustomerId]?.equals(nextFolderId)) {
          return currentId;
        } else {
          return {
            [stringifiedLastCustomerId]: nextFolderId,
          };
        }
      });
    }
  });

  return (
    <>
      <Show when={!isNil(folderQuery().error) && NON_RETRYABLE_STATUS_CODES.includes(folderQuery().error!.status)}>
        <ResourceNotFoundContent
          resource={keyFromSelector(($) => $.RESOURCE_FOLDER)}
          illustration={{ primaryIcon: "folder" }}
        />
      </Show>
      <Show when={folderQuery().data}>
        {(folder) => (
          <div class="flex h-full flex-col">
            <Header folder={folder()} onPermissionAdded={() => setTab("permissions")} />
            <Show
              when={PermissionService.canManagePermissions(customer.customerId)}
              fallback={
                <div class="mt-4 min-h-0 flex-1">
                  <FolderPageContentForms folder={folder()} />
                </div>
              }
            >
              <div class="mt-4 min-h-0 flex-1">
                <GlobalTabs
                  value={tab}
                  onTabChange={setTab}
                  items={[
                    {
                      key: "forms",
                      label: () => t(($) => $.FOLDER_PAGE_FORMS_TAB_TITLE),
                      Slot: () => <FolderPageContentForms folder={folder()} />,
                    },
                    {
                      key: "permissions",
                      label: () => t(($) => $.FOLDER_PAGE_PERMISSIONS_TAB_TITLE),
                      Slot: () => <ResourcePermissionsContent resourceType="FOLDER" resourceId={folder().id} />,
                    },
                  ]}
                />
              </div>
            </Show>
          </div>
        )}
      </Show>
    </>
  );
};

const Header: Component<{ folder: Folder; onPermissionAdded: () => void }> = (props) => {
  const owner = getOwner();
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const canEditFolder = createMemo(() => PermissionService.canEditFolder(customer.customerId, props.folder.id));
  const [isEditingFolderName, setIsEditingFolderName] = createSignal(false);
  const [isEditingFolderDescription, setIsEditingFolderDescription] = createSignal(false);

  const openErrorModal = (err: MoreAppError) => open({ content: () => <GenericFailureModal error={err} /> });

  const openWizard = (folderId: FolderId) =>
    open({
      content: () => <CreateFormWizard customerId={customer.customerId} folderId={folderId} />,
    });

  return (
    <div class="flex gap-4">
      <IconSelect
        icons={SELECTABLE_FOLDER_ICONS}
        value={props.folder.meta.icon}
        size="lg"
        onChange={(value) => {
          const folderId = props.folder.id;
          runWithOwner(owner, () => {
            ApiService.patchFolder(folderId).mutate(
              [
                {
                  op: "replace",
                  path: "/meta/icon",
                  value: toLegacyPlatformIcon(value),
                },
              ],
              {
                onError: openErrorModal,
              },
            );
          });
        }}
        disabled={!canEditFolder()}
      />

      <div class="flex min-w-0 flex-col justify-between">
        <div class="text-header-xl text-primary">
          <InlineEditor
            value={props.folder.meta.name}
            setValue={(value) => {
              const folderId = props.folder.id;
              runWithOwner(owner, () => {
                ApiService.patchFolder(folderId).mutate(
                  [
                    {
                      op: "replace",
                      path: "/meta/name",
                      value: value,
                    },
                  ],
                  {
                    onError: openErrorModal,
                  },
                );
              });
            }}
            label={t(($) => $.FOLDER_PAGE_EDIT_FOLDER_NAME_ARIA_LABEL)}
            open={isEditingFolderName()}
            setOpen={setIsEditingFolderName}
            maxLength={MAX_CHARACTERS_SM}
            schema={pickSchema(pickSchema(folderSchema, "meta"), "name")}
            displayView={() => (
              <div class="flex items-center gap-1">
                <Text as="h2" text={props.folder.meta.name} truncate />
                <Show when={canEditFolder()}>
                  <IconButton
                    icon="pencil"
                    size="medium"
                    variant="tertiary"
                    aria-label={t(($) => $.EDIT_ARIA)}
                    onClick={() => setIsEditingFolderName(true)}
                  />
                </Show>
              </div>
            )}
          />
        </div>

        <div class="text-base-medium text-secondary">
          <InlineEditor<string | undefined>
            value={props.folder.meta.description ?? ""}
            setValue={(value) => {
              const folderId = props.folder.id;
              runWithOwner(owner, () => {
                ApiService.patchFolder(folderId).mutate(
                  [
                    {
                      op: "replace",
                      path: "/meta/description",
                      value: value ?? null,
                    },
                  ],
                  {
                    onError: openErrorModal,
                  },
                );
              });
            }}
            label={t(($) => $.FOLDER_PAGE_EDIT_FOLDER_DESCRIPTION_ARIA_LABEL)}
            open={isEditingFolderDescription()}
            setOpen={setIsEditingFolderDescription}
            maxLength={MAX_CHARACTERS_MD}
            schema={pickSchema(pickSchema(folderSchema, "meta"), "description")}
            displayView={() => (
              <div class="flex items-center gap-1">
                <Switch>
                  <Match when={props.folder.meta.description}>
                    {(description) => <Text as="h2" text={description()} truncate />}
                  </Match>
                  <Match when={canEditFolder()}>
                    <span class="italic">{t(($) => $.FOLDER_PAGE_EDIT_FOLDER_DESCRIPTION_PLACEHOLDER)}</span>
                  </Match>
                </Switch>

                <Show when={canEditFolder()}>
                  <IconButton
                    icon="pencil"
                    size="medium"
                    variant="tertiary"
                    aria-label={t(($) => $.EDIT_ARIA)}
                    onClick={() => setIsEditingFolderDescription(true)}
                  />
                </Show>
              </div>
            )}
          />
        </div>

        <Show when={props.folder.status !== "UNKNOWN"}>
          <PillSingleSelect
            ariaLabel={t(($) => $.FOLDER_PAGE_EDIT_FOLDER_VISIBILITY_ARIA_LABEL)}
            options={[
              {
                variant: "icon",
                value: "ACTIVE",
                label: t(($) => $.FOLDER_PAGE_EDIT_FOLDER_VISIBILITY_OPTIONS_VISIBLE),
                icon: { name: "eye", type: "solid" },
              },
              {
                variant: "icon",
                value: "HIDDEN",
                label: t(($) => $.FOLDER_PAGE_EDIT_FOLDER_VISIBILITY_OPTIONS_HIDDEN),
                icon: { name: "eyeOff", type: "solid" },
              },
            ]}
            onSelect={(value) => {
              const folderId = props.folder.id;
              runWithOwner(owner, () => {
                ApiService.patchFolder(folderId).mutate(
                  [
                    {
                      op: "replace",
                      path: "/status",
                      value: value,
                    },
                  ],
                  {
                    onError: openErrorModal,
                  },
                );
              });
            }}
            disabled={!canEditFolder()}
            value={props.folder.status}
          />
        </Show>
      </div>

      <div class="ml-auto flex items-center gap-2">
        <Show when={PermissionService.canCreateForm(customer.customerId, props.folder.id)}>
          <TextAndIconButton
            icon="plus"
            variant="light"
            text={t(($) => $.CREATE_FORM_BUTTON_LABEL)}
            onClick={() => openWizard(props.folder.id)}
          />
        </Show>
        <Show when={PermissionService.canManagePermissions(customer.customerId)}>
          <IconButton
            icon="userAdd"
            variant="secondary"
            onClick={() =>
              open({
                content: () => (
                  <AddPermissionModal
                    resourceType="FOLDER"
                    resourceId={props.folder.id}
                    onSuccess={props.onPermissionAdded}
                  />
                ),
              })
            }
            aria-label={t(($) => $.FOLDER_PAGE_PERMISSIONS_ADD_USER_ARIA)}
          />
        </Show>
        <Show when={PermissionService.canDeleteFolder(customer.customerId, props.folder.id)}>
          <DeleteFolderButton folder={props.folder} />
        </Show>
      </div>
    </div>
  );
};

export default FolderPage;
